/**
 * useFlushOnReconnect.ts — drive the outbox flush from foreground signals.
 *
 * Mount in a component that is always alive when the technician is authenticated.
 * Triggers (all funnel into flush.run, which is single-flight so overlaps are safe):
 *   - mount + online        → run('start')   (cold-start pending queue)
 *   - window 'online' event → run('online')  (signal recovered while app open)
 *   - tab becomes visible / window focus + online → run('visible')
 *       Covers the mobile case: técnico finalizes offline, pockets the phone, signal
 *       returns, then reopens the PWA — the 'online' event may have fired while the
 *       app was hidden (and been missed), so re-flush on visibility.
 * On unmount: remove all listeners.
 *
 * ponytail: browser-native events, no polling. Ceiling = flush while the app is FULLY
 * closed (not just hidden) — that needs SW Background Sync; documented in design §8.
 */

import { useEffect } from "react";
import { run } from "../services/flush.js";

export function useFlushOnReconnect(): void {
  useEffect(() => {
    const onOnline = () => {
      void run("online");
    };

    const onVisible = () => {
      // Only when the app is actually in front AND has connectivity.
      if (document.visibilityState === "visible" && navigator.onLine) {
        void run("visible");
      }
    };

    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    // Flush immediately if already online at mount (cold-start pending queue).
    if (navigator.onLine) {
      void run("start");
    }

    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
}
